// Utility to validate and fix hours data consistency

export const validateRouteHours = (route) => {
  if (!route) return null;
  
  // Check if the values make sense
  const issues = [];
  
  // Service time should be in minutes
  if (route.totalServiceTime) {
    // If it's less than 60 and we have multiple stops, it's probably in hours
    if (route.totalServiceTime < 60 && route.stops?.length > 1) {
      issues.push({
        field: 'totalServiceTime',
        value: route.totalServiceTime,
        issue: 'Suspiciously low for minutes - might be in hours',
        suggestedFix: route.totalServiceTime * 60
      });
    }
    
    // If it's more than 600 (10 hours) for a single stop, might be wrong
    if (route.totalServiceTime > 600 && route.stops?.length === 1) {
      issues.push({
        field: 'totalServiceTime',
        value: route.totalServiceTime,
        issue: 'Suspiciously high for single stop',
        suggestedFix: route.totalServiceTime / 60
      });
    }
  }
  
  // Drive time should be in minutes
  if (route.totalDriveTime) {
    // Drive time shouldn't exceed service time by too much
    if (route.totalDriveTime > route.totalServiceTime * 2) {
      issues.push({
        field: 'totalDriveTime',
        value: route.totalDriveTime,
        issue: 'Drive time seems too high compared to service time'
      });
    }
  }
  
  // Calculate expected service time from stops
  if (route.stops?.length > 0) {
    const expectedServiceMinutes = route.stops.reduce((total, stop) => {
      return total + ((stop.estimatedHours || 0) * 60);
    }, 0);
    
    const actualServiceMinutes = route.totalServiceTime || 0;
    const difference = Math.abs(expectedServiceMinutes - actualServiceMinutes);
    
    if (difference > 5) { // Allow 5 minute tolerance
      issues.push({
        field: 'totalServiceTime',
        value: actualServiceMinutes,
        expected: expectedServiceMinutes,
        difference: difference,
        issue: 'Service time doesn\'t match sum of stop hours'
      });
    }
  }
  
  if (issues.length > 0) {
    console.warn('⚠️ Route validation issues:', {
      crewId: route.crewId,
      crewName: route.crewName,
      issues
    });
  }
  
  return issues;
};

export const validateWeeklyHours = (weeklyHours, weeklyRoutes) => {
  console.log('🔍 Validating weekly hours consistency...');
  
  const issues = [];
  
  Object.keys(weeklyHours).forEach(crewId => {
    const crewHours = weeklyHours[crewId];
    
    // Check if any day has suspiciously high hours
    ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].forEach(day => {
      const dayData = crewHours[day];
      if (dayData) {
        const totalHours = (dayData.serviceTime || 0) + (dayData.driveTime || 0);
        
        if (totalHours > 24) {
          issues.push({
            crewId,
            day,
            totalHours,
            issue: 'Day total exceeds 24 hours - data might be in wrong units'
          });
        }
        
        if (totalHours > 12) {
          console.warn(`⚠️ High hours for crew ${crewId} on ${day}: ${totalHours.toFixed(2)}h`);
        }
      }
    });
    
    // Validate weekly total
    const weekTotal = (crewHours.total?.serviceTime || 0) + (crewHours.total?.driveTime || 0);
    if (weekTotal > 120) { // More than 24h/day average
      issues.push({
        crewId,
        weekTotal,
        issue: 'Week total seems too high'
      });
    }
  });
  
  if (issues.length > 0) {
    console.error('❌ Weekly hours validation failed:', issues);
  } else {
    console.log('✅ Weekly hours validation passed');
  }
  
  return issues;
};

export const ensureMinutesNotHours = (value, fieldName) => {
  // If the value is suspiciously low (less than 60) and represents time,
  // it might be in hours instead of minutes
  if (value && value < 60) {
    console.warn(`⚠️ ${fieldName} might be in hours (${value}), converting to minutes: ${value * 60}`);
    return value * 60;
  }
  return value;
};

export const ensureHoursNotMinutes = (value, fieldName) => {
  // If the value is suspiciously high (more than 24) and represents hours,
  // it might be in minutes instead of hours
  if (value && value > 24) {
    console.warn(`⚠️ ${fieldName} might be in minutes (${value}), converting to hours: ${value / 60}`);
    return value / 60;
  }
  return value;
};